import type { ChatState } from "../controllers/chat.ts";
import { clearLiveState, recordLiveState } from "./chat-live-state.ts";
import { dropStreamingTail } from "./chat-streaming-tail.ts";
import { stopChatWaitTicker, syncChatWaitTicker } from "./chat-wait-ticker.ts";

export function reconcileStaleChatRunFromHistory(
  state: ChatState,
  activeClientRunId: string | null | undefined,
): void {
  const latched = state.chatRunId?.trim();
  if (!latched || state.chatSending) {
    return;
  }
  const active =
    typeof activeClientRunId === "string" && activeClientRunId.trim()
      ? activeClientRunId.trim()
      : null;
  if (active === latched) {
    return;
  }
  state.chatRunId = null;
  state.chatStream = null;
  state.chatReasoningStream = null;
  state.chatLiveRunHint = null;
  state.chatStreamStartedAt = null;
  if ("chatCollectQueueDepth" in state) {
    (state as { chatCollectQueueDepth?: number }).chatCollectQueueDepth = 0;
  }
  stopChatWaitTicker(state as unknown as Parameters<typeof stopChatWaitTicker>[0]);
  clearLiveState(state, state.sessionKey);
  if (Array.isArray(state.chatMessages)) {
    const trimmed = dropStreamingTail(state.chatMessages, latched);
    if (trimmed !== state.chatMessages) {
      state.chatMessages = trimmed;
    }
  }
}

export function bootstrapLiveRunFromHistory(
  state: ChatState,
  activeClientRunId: string | null | undefined,
  params?: { startedAt?: number },
): boolean {
  const active =
    typeof activeClientRunId === "string" && activeClientRunId.trim()
      ? activeClientRunId.trim()
      : null;
  if (!active) {
    return false;
  }
  const existing = state.chatRunId?.trim();
  if (existing) {
    return false;
  }
  if (state.chatSending) {
    return false;
  }
  const now = typeof params?.startedAt === "number" ? params.startedAt : Date.now();
  state.chatRunId = active;
  state.chatStream = state.chatStream ?? "";
  state.chatReasoningStream = state.chatReasoningStream ?? "";
  if (state.chatStreamStartedAt == null) {
    state.chatStreamStartedAt = now;
  }
  recordLiveState(state, state.sessionKey, {
    runId: active,
    startedAt: now,
    stream: state.chatStream,
    reasoningStream: state.chatReasoningStream,
    liveRunHint: state.chatLiveRunHint ?? null,
    activeFileToolPath: state.chatActiveFileToolPath ?? null,
    mainRun: state.chatMainRun ?? null,
    compaction: state.compactionStatus ?? null,
    injectedAt: state.chatInjectedAt ?? null,
    collectQueueDepth: state.chatCollectQueueDepth ?? 0,
  });
  syncChatWaitTicker(state as unknown as Parameters<typeof syncChatWaitTicker>[0]);
  return true;
}

/** Single entry point: reconcile + bootstrap from `chat.history.activeClientRunId`. */
export function syncChatRunFromServerHistory(
  state: ChatState,
  params?: { activeClientRunId?: string | null; startedAt?: number },
): void {
  reconcileStaleChatRunFromHistory(state, params?.activeClientRunId);
  bootstrapLiveRunFromHistory(state, params?.activeClientRunId, {
    startedAt: params?.startedAt,
  });
}
